import { optionalText, parseDate, splitCsv } from "~/shared/lib/filterHelpers";

export type IBaseStaffUrlParams = {
  page?: string;
  count?: string;
  sortField?: string;
  sortType?: string;
  openModal?: string;
  currentId?: string;
  searchText?: string;
  id?: string;
  surname?: string;
  name?: string;
  middleName?: string;
  status?: string;
  email?: string;
  workPhone?: string;
  emailVerification?: string;
  ladaAcademiaId?: string;
  positionIds?: string;
  filterBy?: string;
  filterValues?: string;
  creationFrom?: string;
  creationTo?: string;
  lastEditFrom?: string;
  lastEditTo?: string;
};

export type SelectOption = {
  value: string;
  label: string;
};

export interface IBaseStaffFilterForm {
  id?: string;
  fullname?: string;
  surname?: string;
  name?: string;
  middleName?: string;
  status?: string;
  email?: string;
  workPhone?: string;
  emailVerification?: { value: boolean | null; label: string } | null;
  ladaAcademiaId?: string;
  positionIds: SelectOption[];
  filterBy?: string;
  filterValues: SelectOption[];
  creationFrom?: Date | null;
  creationTo?: Date | null;
  lastEditFrom?: Date | null;
  lastEditTo?: Date | null;
}

export const BASE_STAFF_FILTER_URL_KEYS = [
  "id",
  "surname",
  "name",
  "middleName",
  "status",
  "email",
  "workPhone",
  "emailVerification",
  "ladaAcademiaId",
  "positionIds",
  "filterBy",
  "filterValues",
  "creationFrom",
  "creationTo",
  "lastEditFrom",
  "lastEditTo",
] as const;

export const joinEmployeeFullName = (
  surname?: string,
  name?: string,
  middleName?: string,
) => [surname, name, middleName].filter(Boolean).join(" ");

export const splitEmployeeFullName = (value?: string) => {
  const parts = (value || "").trim().split(/\s+/).filter(Boolean);

  return {
    surname: parts[0],
    name: parts[1],
    middleName: parts.length > 2 ? parts.slice(2).join(" ") : undefined,
  };
};

export const clearBaseStaffFilterUrlParams = () =>
  BASE_STAFF_FILTER_URL_KEYS.reduce<Record<string, undefined>>((acc, key) => {
    acc[key] = undefined;
    return acc;
  }, {});

const parseEmailVerification = (value?: string | null) => {
  if (value === "true") {
    return { value: true, label: "" };
  }
  if (value === "false") {
    return { value: false, label: "" };
  }
  return null;
};

export const parseBaseStaffFilterFromUrl = (
  params: IBaseStaffUrlParams,
  dealerOptions: ReadonlyArray<SelectOption> = [],
  positionOptions: ReadonlyArray<SelectOption> = [],
  partnerOptions: ReadonlyArray<SelectOption> = [],
): IBaseStaffFilterForm => {
  const mapIdsToOptions = (
    ids: string[],
    options: ReadonlyArray<SelectOption>,
  ): SelectOption[] =>
    ids.map(
      (id) =>
        options.find((item) => item.value === id) || { value: id, label: id },
    );

  const filterBy = optionalText(params.filterBy);
  const rawFilterValues = splitCsv(params.filterValues);
  const filterValues =
    filterBy === "dealer"
      ? mapIdsToOptions(rawFilterValues, dealerOptions)
      : filterBy === "partner"
        ? mapIdsToOptions(rawFilterValues, partnerOptions)
        : rawFilterValues.map((value) => ({ value, label: value }));

  return {
    id: optionalText(params.id),
    fullname: joinEmployeeFullName(
      params.surname,
      params.name,
      params.middleName,
    ),
    surname: optionalText(params.surname),
    name: optionalText(params.name),
    middleName: optionalText(params.middleName),
    status: optionalText(params.status),
    email: optionalText(params.email),
    workPhone: optionalText(params.workPhone),
    emailVerification: parseEmailVerification(params.emailVerification),
    ladaAcademiaId: optionalText(params.ladaAcademiaId),
    positionIds: mapIdsToOptions(splitCsv(params.positionIds), positionOptions),
    filterBy,
    filterValues,
    creationFrom: parseDate(params.creationFrom),
    creationTo: parseDate(params.creationTo),
    lastEditFrom: parseDate(params.lastEditFrom),
    lastEditTo: parseDate(params.lastEditTo),
  };
};
